import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eyebrow, Button, HUDBrackets } from './UI';
type Hotspot = {
  id: string;
  tag: string;
  name: string;
  desc: string;
  // Position on image (percentages)
  top: string;
  left: string;
  // Side the label sits on
  side: 'left' | 'right';
};
const hotspots: Hotspot[] = [{
  id: 'box',
  tag: '01',
  name: 'TACTICAL FIELD BOX',
  desc: 'Camo-printed collector packaging. Built to ship, built to display.',
  top: '22%',
  left: '28%',
  side: 'left'
}, {
  id: 'mask',
  tag: '02',
  name: 'BLACKOUT UNIT MASK™',
  desc: 'Glass-Fiber Composite shell with Grade 304 stainless skeleton.',
  top: '52%',
  left: '72%',
  side: 'right'
}, {
  id: 'lenses',
  tag: '03',
  name: 'SMOKE BLACK LENSES',
  desc: 'Polycarbonate ANSI Z87.1 — anti-fog, UV400. Pre-installed.',
  top: '40%',
  left: '78%',
  side: 'right'
}, {
  id: 'strap',
  tag: '04',
  name: 'BALLISTIC NYLON STRAP',
  desc: 'MOLLE-spec dual-point adjust. Fits 52cm–62cm.',
  top: '68%',
  left: '22%',
  side: 'left'
}, {
  id: 'padding',
  tag: '05',
  name: 'NEOPRENE COMFORT PADDING',
  desc: 'Military-spec inner padding. Anti-bacterial, moisture-wicking.',
  top: '82%',
  left: '60%',
  side: 'right'
}];
export const WhatsInBox = () => {
  const [activeId, setActiveId] = useState<string>('mask');
  return <section className="bg-gradient-to-b from-dark-1 to-base-black pt-[40px] pb-[40px]" data-id="element-737">
      <div className="container mx-auto px-6 md:px-12" data-id="element-738">
        <div className="text-center mb-16" data-id="element-739">
          <Eyebrow data-id="element-740">FULL KIT INVENTORY</Eyebrow>
          <h2 className="text-5xl md:text-[64px] leading-[0.9] mb-4" data-id="element-741">
            EVERYTHING THAT SHIPS WITH YOUR{' '}
            <span className="text-acid-green" data-id="element-742">UNIT</span>.
          </h2>
          <p className="font-barlow text-lg text-text-body max-w-2xl mx-auto" data-id="element-743">
            Mission-ready out of the box. Hover or tap any marker to inspect the
            kit.
          </p>
        </div>

        {/* Interactive Hotspot Diagram */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 lg:gap-16 max-w-[1200px] mx-auto items-center mb-20" data-id="element-744">
          {/* Image with hotspots */}
          <div className="relative border border-acid-green/20 bg-dark-1 aspect-square overflow-hidden" data-id="element-745">
            <HUDBrackets data-id="element-746" />
            <div className="absolute top-4 left-4 font-barlow text-[10px] text-acid-green tracking-[2px] z-20 border border-acid-green/40 px-2 py-1 bg-base-black/60" data-id="element-747">
              KIT INSPECTION — 05 ITEMS
            </div>

            <img src="https://cdn.magicpatterns.com/uploads/7fnAZ9tC6TNigxzBPNFtQB/hf_20260505_161401_b22ab001-c648-4408-a098-97bb18fba3a3.png" alt="BLACKOUT UNIT Mask & Box Kit" className="absolute inset-0 w-full h-full object-cover" data-id="element-748" />
            {/* Subtle vignette */}
            <div className="absolute inset-0 bg-gradient-to-t from-base-black/50 via-transparent to-base-black/20 pointer-events-none" data-id="element-749" />

            {/* Hotspots */}
            {hotspots.map(h => {
            const isActive = activeId === h.id;
            return <button key={h.id} onMouseEnter={() => setActiveId(h.id)} onClick={() => setActiveId(h.id)} className="absolute z-10 -translate-x-1/2 -translate-y-1/2 group" style={{
              top: h.top,
              left: h.left
            }} aria-label={h.name} data-id="element-750">
                  {/* Pulse ring */}
                  <span className={`absolute inset-0 m-auto w-8 h-8 rounded-full border-2 border-acid-green ${isActive ? 'animate-ping opacity-60' : 'opacity-0'}`} data-id="element-751" />
                  {/* Outer ring */}
                  <span className={`relative block w-8 h-8 rounded-full border-2 transition-all ${isActive ? 'border-acid-green bg-acid-green/20 scale-110' : 'border-white/70 bg-black/40 group-hover:border-acid-green'}`} data-id="element-752">
                    {/* Inner dot */}
                    <span className={`absolute inset-0 m-auto w-3 h-3 rounded-full transition-colors ${isActive ? 'bg-acid-green' : 'bg-white group-hover:bg-acid-green'}`} style={{
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }} data-id="element-753" />
                  </span>
                  {/* Tag number floating above */}
                  <span className={`absolute -top-6 left-1/2 -translate-x-1/2 font-bebas text-[14px] tracking-[2px] transition-colors ${isActive ? 'text-acid-green' : 'text-white/80'}`} data-id="element-754">
                    {h.tag}
                  </span>
                </button>;
          })}
          </div>

          {/* Side list (acts as legend + active spotlight) */}
          <div data-id="element-755">
            <div className="font-barlow text-[10px] text-text-dimmed tracking-[2px] mb-4 uppercase" data-id="element-756">
              ━━ Inventory Manifest ━━
            </div>
            <div className="space-y-2" data-id="element-757">
              {hotspots.map(h => {
              const isActive = activeId === h.id;
              return <button key={h.id} onMouseEnter={() => setActiveId(h.id)} onClick={() => setActiveId(h.id)} className={`w-full text-left flex items-start gap-4 p-4 border transition-all ${isActive ? 'border-acid-green bg-acid-green-dim' : 'border-dark-3 bg-dark-1 hover:border-acid-green/40'}`} data-id="element-758">
                    <div className={`shrink-0 w-9 h-9 border flex items-center justify-center font-bebas text-[16px] transition-colors ${isActive ? 'border-acid-green text-acid-green' : 'border-dark-3 text-text-dimmed'}`} data-id="element-759">
                      {h.tag}
                    </div>
                    <div className="min-w-0" data-id="element-760">
                      <h4 className="font-bebas text-[18px] text-white leading-none mb-1" data-id="element-761">
                        {h.name}
                      </h4>
                      <p className="font-inter text-[12px] text-text-dimmed leading-snug" data-id="element-762">
                        {h.desc}
                      </p>
                    </div>
                    {isActive && <motion.div layoutId="kit-indicator" className="w-1 self-stretch bg-acid-green shrink-0" data-id="element-763" />}
                  </button>;
            })}
            </div>
          </div>
        </div>

        {/* Value Block */}
      </div>
    </section>;
};